import { Suspense, lazy, useRef, useState, useEffect, type ComponentType } from "react";

interface LazySectionProps {
  factory: () => Promise<{ default: ComponentType<any> }>;
  fallbackHeight?: string;
}

export function LazySection({ factory, fallbackHeight = "h-96" }: LazySectionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [Component, setComponent] = useState<ComponentType<any> | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldLoad) return;
    const LazyComp = lazy(factory);
    setComponent(() => LazyComp);
  }, [shouldLoad, factory]);

  return (
    <div ref={ref}>
      {Component ? (
        <Suspense fallback={<div className={fallbackHeight} />}>
          <Component />
        </Suspense>
      ) : (
        <div className={fallbackHeight} />
      )}
    </div>
  );
}
